import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import {
  getBinaryPreference,
  getSlotStanding,
  setBinaryPreference,
  type BinaryPreferenceStatus,
} from '../lib/api';

const SIDES: ReadonlyArray<{ value: 'L' | 'R'; label: string }> = [
  { value: 'L', label: 'Left' },
  { value: 'R', label: 'Right' },
];

/**
 * Extreme Binary spillover-side control for the currently-selected slot: the
 * member's recruits spill straight down THIS side of their binary tree. Changing
 * it affects FUTURE placements only — it never moves already-placed downlines.
 *
 * The L/R toggle is GATED by `canChange` (the server's directs requirement); when
 * locked it renders disabled with the "unlocks after N directs" hint.
 *
 * Visibility: this only applies to the single-side-spillover `extreme` variant.
 * The standing endpoint reports the true variant (`getSlotStanding` maps
 * `variant_id === 'extreme'`), so we gate strictly on `=== 'extreme'` — the side
 * preference is a no-op under legacy/teslab placement.
 */
export function BinarySidePreference({ slotId }: { slotId: string }) {
  const qc = useQueryClient();

  // Shares the dashboard's ['standing', slotId] cache. Only the single-side
  // spillover `extreme` variant honors a per-slot side preference.
  const standingQ = useQuery({
    queryKey: ['standing', slotId],
    queryFn: () => getSlotStanding(slotId),
    enabled: !!slotId,
  });
  const applicable = standingQ.data?.binaryVariant === 'extreme';

  const prefQ = useQuery({
    queryKey: ['binary-preference', slotId],
    queryFn: () => getBinaryPreference(slotId),
    enabled: !!slotId && applicable,
  });

  const mutation = useMutation({
    mutationFn: (side: 'L' | 'R') => setBinaryPreference(slotId, side),
    meta: {
      successMessage: 'Spillover side updated. New placements will follow it.',
      errorMessage: 'Could not update your spillover side.',
    },
    onSuccess: (data: BinaryPreferenceStatus) => {
      qc.setQueryData(['binary-preference', slotId], data);
      qc.invalidateQueries({ queryKey: ['binary-preference', slotId] });
    },
  });

  if (!applicable || !prefQ.data) return null;

  const { side, directs, requirement, canChange } = prefQ.data;
  const lockHint = `Unlocks after ${requirement} ${requirement === 1 ? 'direct' : 'directs'} — you have ${directs}.`;
  const disabled = !canChange || mutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Binary spillover side</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          New recruits spill straight down this side of your binary tree. Changing it affects future
          placements only — it never moves anyone already placed.
        </p>

        <div
          className="flex gap-2"
          role="radiogroup"
          aria-label="Binary spillover side"
          title={canChange ? undefined : lockHint}
        >
          {SIDES.map((s) => {
            const active = side === s.value;
            return (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled}
                onClick={() => {
                  if (!active) mutation.mutate(s.value);
                }}
                className={`flex-1 rounded-md border py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60 ${
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'bg-card hover:bg-accent'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {canChange ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0" />
            Currently spilling to the {side === 'L' ? 'left' : 'right'}.
          </p>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            {lockHint}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
